// commission-payout-report/render.ts — the pure part of the commission payout report (Oct 28, 2026): the
// approved bills in, the subject / HTML / plain text out. No Deno, browser or Supabase APIs, so the portal's
// unit tests (src/lib/commissionPayoutReport.test.ts) import it and the edge function imports it unchanged.
//
// A bill is one books_payout_bills row — one person, one approval click — with its lines, as
// commission_payout_report_data() returns it. Each line carries its job, its label (books_payout_line_label) and
// the math Books' pay emails print (books_payout_line_detail: "Revenue $6,946.20 (your half of the job) × 12%",
// "Split with Briggs Clayton", "Less Non-collect deduction: $123.45"). That text was written to the payee; the
// report is read by Shannon and Greg, so "your half of the job" reads "half of the job" here and nothing else is
// reworded.
// Money is the bill's total (what Books pays); the lines are its detail.

export interface ReportLine {
  line_type: string;
  label: string;
  amount: number;
  deal_id: string | null;
  memo: string | null;
  /** "Homeowner — street" (books_job), null for a line with no job (a TL month). */
  job: string | null;
  detail: string[];
}

export interface ReportBill {
  id: string;
  kind: string;
  batch_id: string | null;
  /** books_payout_bills.status: held | sent | part_paid | paid | voided */
  status: string;
  total: number;
  approved_at: string;
  approved_by: string | null;
  approved_by_email: string | null;
  hold_reason: string | null;
  paid_on: string | null;
  paid_method: string | null;
  voided_at: string | null;
  payee_id: string;
  payee: string | null;
  role: string | null;
  lines: ReportLine[];
}

export interface PayoutReport {
  bills: ReportBill[];
  /** The Commission board's Approved tab. */
  boardUrl: string;
  /** finance_settings commission_payout_report_kinds, for the footer. */
  kinds: string[];
  /** Who pressed "Send to Shannon" (null on a preview). */
  sentBy: string | null;
}

// ── reading what the database sent ───────────────────────────────────────────────────────────────────────────

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);
const money = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
};
const round2 = (n: number) => Math.round(n * 100) / 100;

/** commission_payout_report_data()'s JSON → bills; a row without an id, a payee or an approval time is dropped. */
export function normalizeBills(raw: unknown): ReportBill[] {
  if (!Array.isArray(raw)) return [];
  const out: ReportBill[] = [];
  for (const r of raw as Array<Record<string, unknown>>) {
    if (!r || typeof r !== "object") continue;
    const id = str(r.id);
    const payeeId = str(r.payee_id);
    const approvedAt = str(r.approved_at);
    if (!id || !payeeId || !approvedAt || !Number.isFinite(Date.parse(approvedAt))) continue;
    const lines: ReportLine[] = [];
    for (const l of (Array.isArray(r.lines) ? r.lines : []) as Array<Record<string, unknown>>) {
      if (!l || typeof l !== "object") continue;
      const type = str(l.line_type) ?? "line";
      lines.push({
        line_type: type,
        label: str(l.label) ?? type,
        amount: money(l.amount),
        deal_id: str(l.deal_id),
        memo: str(l.memo),
        job: str(l.job),
        detail: (Array.isArray(l.detail) ? l.detail : []).map((d) => str(d)).filter((d): d is string => d !== null),
      });
    }
    out.push({
      id, kind: str(r.kind) ?? "commission", batch_id: str(r.batch_id), status: str(r.status) ?? "sent",
      total: money(r.total), approved_at: approvedAt, approved_by: str(r.approved_by),
      approved_by_email: str(r.approved_by_email)?.toLowerCase() ?? null, hold_reason: str(r.hold_reason),
      paid_on: str(r.paid_on), paid_method: str(r.paid_method), voided_at: str(r.voided_at),
      payee_id: payeeId, payee: str(r.payee), role: str(r.role), lines,
    });
  }
  return out;
}

/** What is still going to be paid: a bill withdrawn (un-approved) before the report went is left off. */
export function liveBills(bills: ReportBill[]): ReportBill[] {
  return bills.filter((b) => b.status !== "voided" && !b.voided_at && b.total > 0);
}

// ── formatting ────────────────────────────────────────────────────────────────────────────────────────────────

export function fmtUSD(n: number): string {
  const v = round2(n);
  const s = "$" + Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? "−" + s : s;
}

export function escHtml(s: string | null | undefined): string {
  if (!s) return "";
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c]);
}

const KEEP_CAPS = /^(II|III|IV|LLC|INC|LP|LLP|PC)$/;
/** "CHRISTOPHER ALLEN Perry" → "Christopher Allen Perry"; mixed-case words are left alone. */
export function tidyName(raw: string | null | undefined): string {
  const s = (raw ?? "").trim();
  if (!s) return "(no name)";
  return s.split(/\s+/).map((w) =>
    w.length > 1 && w === w.toUpperCase() && /[A-Z]/.test(w) && !KEEP_CAPS.test(w) ? w.charAt(0) + w.slice(1).toLowerCase() : w,
  ).join(" ");
}

const ROLE_LABELS: Record<string, string> = {
  restore_manager: "RM", team_lead: "TL", asm: "ASM", sales_director: "Sales director", roof_specialist: "RS",
  roof_team_manager: "Roof team manager", roof_director: "Roof director", director_of_operations: "Director of operations",
  qc: "QC", finance: "Finance", admin: "Admin", canvasser: "Canvasser", project_manager: "Project manager",
  office: "Office", drone_pilot: "Drone pilot", report_specialist: "Report specialist",
};
export function roleLabel(role: string | null): string {
  if (!role) return "";
  return ROLE_LABELS[role] ?? role.replace(/_/g, " ");
}

// the order of a person's lines: books_payout_line_rank (own jobs, the trade upside pool and true-ups, TL override,
// ASM override, RS pool, …). trade_upside (Oct 29, 2026) sits with the person's own jobs, as the SQL rank does.
const LINE_RANK: Record<string, number> = {
  rm: 1, rm_split: 1, trade_upside: 2, trueup: 2, tl_override: 3, asm_override: 4, rs_pool: 5, crew: 6, pm: 7, spiff: 8, canvasser: 9, tl_bonus: 10,
};
const rankOf = (t: string) => LINE_RANK[t] ?? 11;

/** "Richard Rhinehart — 1815 Rockybranch Pass" → name + where; a line with no job uses its label and memo. */
export function jobParts(l: ReportLine): { name: string; where: string } {
  if (l.job) {
    const i = l.job.indexOf(" — ");
    return i >= 0
      ? { name: tidyName(l.job.slice(0, i)), where: l.job.slice(i + 3).trim() }
      : { name: tidyName(l.job), where: "" };
  }
  return { name: l.label, where: l.memo && l.memo !== l.label ? l.memo : "" };
}

/** Books' detail lines, in the third person instead of to the payee. */
export function detailFor(l: ReportLine): string[] {
  return l.detail.map((d) => d.replace(/\(your half of the job\)/g, "(half of the job)"));
}

const ET = "America/New_York";
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;

/** "YYYY-MM-DD" of a moment, in Eastern time. */
export function etDay(ts: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: ET, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(ts));
}
/** "9:03 AM" of a moment, in Eastern time (newer ICU puts a narrow no-break space before AM: made a plain space). */
export function etTime(ts: string): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: ET, hour: "numeric", minute: "2-digit" }).format(new Date(ts)).replace(/\s+/g, " ");
}
/** "2026-10-16" → "Oct 16, 2026" (a date-only value: no time zone can move it). */
export function fmtDay(day: string): string {
  const m = DAY_RE.exec(day);
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}` : "";
}
/** "2026-10-28" → "Wednesday". */
export function weekday(day: string): string {
  const m = DAY_RE.exec(day);
  return m ? WEEKDAYS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()] : "";
}
/** One day: "Oct 28, 2026"; a span: "Oct 27 – 28, 2026", "Oct 31 – Nov 2, 2026", "Dec 31, 2026 – Jan 2, 2027". */
export function daySpan(first: string, last: string): string {
  const a = DAY_RE.exec(first), b = DAY_RE.exec(last);
  if (!a || !b) return fmtDay(first) || fmtDay(last);
  if (first.slice(0, 10) === last.slice(0, 10)) return fmtDay(first);
  if (a[1] !== b[1]) return `${fmtDay(first)} – ${fmtDay(last)}`;
  if (a[2] !== b[2]) return `${MONTHS[Number(a[2]) - 1]} ${Number(a[3])} – ${MONTHS[Number(b[2]) - 1]} ${Number(b[3])}, ${b[1]}`;
  return `${MONTHS[Number(a[2]) - 1]} ${Number(a[3])} – ${Number(b[3])}, ${b[1]}`;
}
const plural = (n: number, one: string, many = one + "s") => `${n} ${n === 1 ? one : many}`;

const METHOD_LABELS: Record<string, string> = { ach: "ACH", zelle: "Zelle", check: "check", wire: "wire", cash: "cash" };

// ── the shape of the report ──────────────────────────────────────────────────────────────────────────────────

export interface PersonLine { line: ReportLine; bill: ReportBill }

export interface PersonGroup {
  payeeId: string;
  name: string;
  role: string;
  total: number;
  jobs: number;
  lines: PersonLine[];
}

/** One group per person, the biggest total first; lines in the board's order (own jobs, then overrides …), then by job. */
export function groupByPerson(bills: ReportBill[]): PersonGroup[] {
  const groups = new Map<string, PersonGroup>();
  for (const b of liveBills(bills)) {
    const g = groups.get(b.payee_id) ?? { payeeId: b.payee_id, name: tidyName(b.payee), role: roleLabel(b.role), total: 0, jobs: 0, lines: [] };
    g.total = round2(g.total + b.total);
    for (const line of b.lines) g.lines.push({ line, bill: b });
    groups.set(b.payee_id, g);
  }
  for (const g of groups.values()) {
    g.jobs = new Set(g.lines.map((x) => x.line.deal_id).filter(Boolean)).size;
    g.lines.sort((x, y) =>
      rankOf(x.line.line_type) - rankOf(y.line.line_type)
      || jobParts(x.line).name.localeCompare(jobParts(y.line).name)
      || y.line.amount - x.line.amount);
  }
  return [...groups.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name));
}

export interface Approval { by: string; email: string | null; deals: number; batches: number; first: string; last: string }

/** Who approved, how many deals (or approvals, for lines with no job), and when. */
export function approvals(bills: ReportBill[]): Approval[] {
  const by = new Map<string, { email: string | null; deals: Set<string>; batches: Set<string>; first: string; last: string }>();
  for (const b of liveBills(bills)) {
    const who = b.approved_by ? tidyName(b.approved_by) : "Someone";
    const a = by.get(who) ?? { email: b.approved_by_email, deals: new Set<string>(), batches: new Set<string>(), first: b.approved_at, last: b.approved_at };
    for (const l of b.lines) if (l.deal_id) a.deals.add(l.deal_id);
    a.batches.add(b.batch_id ?? b.id);
    if (Date.parse(b.approved_at) < Date.parse(a.first)) a.first = b.approved_at;
    if (Date.parse(b.approved_at) > Date.parse(a.last)) a.last = b.approved_at;
    by.set(who, a);
  }
  return [...by.entries()]
    .map(([who, a]) => ({ by: who, email: a.email, deals: a.deals.size, batches: a.batches.size, first: a.first, last: a.last }))
    .sort((x, y) => Date.parse(x.first) - Date.parse(y.first));
}

/** "9:03 – 9:27 AM", "9:03 AM", "11:47 AM – 12:38 PM"; across days "Oct 27, 4:10 PM – Oct 28, 9:27 AM". */
export function timeSpan(first: string, last: string): string {
  const d1 = etDay(first), d2 = etDay(last);
  const t1 = etTime(first), t2 = etTime(last);
  if (d1 !== d2) {
    const short = (d: string) => fmtDay(d).replace(/, \d{4}$/, "");
    return `${short(d1)}, ${t1} – ${short(d2)}, ${t2}`;
  }
  if (t1 === t2) return t1;
  const [h1, ap1] = t1.split(" "), [h2, ap2] = t2.split(" ");
  return ap1 === ap2 ? `${h1} – ${h2} ${ap2}` : `${t1} – ${t2}`;
}

export function approvalSentence(a: Approval): string {
  const what = a.deals > 0 ? plural(a.deals, "deal") : plural(a.batches, "payout");
  return `${a.by} approved ${what}, ${timeSpan(a.first, a.last)} ET`;
}

export interface ReportSummary {
  total: number;
  people: number;
  jobs: number;
  bills: number;
  firstDay: string;
  lastDay: string;
  held: { total: number; bills: number };
}

export function summarize(bills: ReportBill[]): ReportSummary {
  const live = liveBills(bills);
  const days = live.map((b) => etDay(b.approved_at)).sort();
  const held = live.filter((b) => b.status === "held");
  return {
    total: round2(live.reduce((s, b) => s + b.total, 0)),
    people: new Set(live.map((b) => b.payee_id)).size,
    jobs: new Set(live.flatMap((b) => b.lines.map((l) => l.deal_id)).filter(Boolean)).size,
    bills: live.length,
    firstDay: days[0] ?? "",
    lastDay: days[days.length - 1] ?? "",
    held: { total: round2(held.reduce((s, b) => s + b.total, 0)), bills: held.length },
  };
}

export function reportSubject(r: PayoutReport): string {
  const s = summarize(r.bills);
  const when = daySpan(s.firstDay, s.lastDay);
  return `Commission payouts approved${when ? ` ${when}` : ""} — ${fmtUSD(s.total)} to ${plural(s.people, "person", "people")}`;
}

/** A bill that is not simply waiting in Books says so: "Held: …", "Paid Oct 28, 2026 (ACH)", "Partly paid in Books". */
export function billStatusNote(b: ReportBill): string | null {
  if (b.status === "held") return `Held${b.hold_reason ? `: ${b.hold_reason}` : " until the hold clears"}`;
  if (b.status === "paid") {
    const how = b.paid_method ? METHOD_LABELS[b.paid_method.toLowerCase()] ?? b.paid_method : null;
    return `Paid${b.paid_on ? ` ${fmtDay(b.paid_on)}` : ""}${how ? ` (${how})` : ""}`;
  }
  if (b.status === "part_paid") return "Partly paid in Books";
  return null;
}

const KIND_WORDS: Record<string, string> = {
  commission: "RM, TL and ASM commissions", rs_pool: "the roof team (RS) pool", tl_bonus: "team lead incentives",
  crew: "crew pay", canvasser: "canvassing", pm: "the project management fee", spiff: "SPIFFs",
};
const ORDER = ["commission", "rs_pool", "tl_bonus", "crew", "pm", "canvasser", "spiff"];

/** "It lists RM, TL and ASM commissions, … approved since the last report …" */
export function footerText(r: PayoutReport): string {
  const kinds = [...new Set(r.kinds)].sort((a, b) => (ORDER.indexOf(a) + 1 || 99) - (ORDER.indexOf(b) + 1 || 99));
  const words = kinds.map((k) => KIND_WORDS[k] ?? k.replace(/_/g, " "));
  const list = words.length <= 1 ? words.join("") : `${words.slice(0, -1).join(", ")} and ${words[words.length - 1]}`;
  return `It lists ${list || "the approved payouts"} approved since the last report, as Books received them; `
    + `"Amount set by hand" means the amount was typed in on the board instead of revenue × rate.`;
}

/** "Chad Bailey approved 6 deals, 9:03 – 9:27 AM ET. Sent by Chad Bailey from the Commission board." */
export function introText(r: PayoutReport): string {
  const who = approvals(r.bills).map((a) => approvalSentence(a) + ".").join(" ");
  const sent = r.sentBy ? `Sent by ${tidyName(r.sentBy)} from the Commission board.` : "";
  return [who, sent].filter(Boolean).join(" ");
}

export const PAY_NOTE = "Each payout below is already a bill in Books, ready to pay.";

// ── HTML ────────────────────────────────────────────────────────────────────────────────────────────────────

const ORANGE = "#f46530";
const INK = "#22272a";
const MUTED = "#64748b";
const RULE = "#eceef0";
const TH = "padding:9px 12px;font-size:11px;letter-spacing:.04em;text-transform:uppercase;color:#7a8085;";

function nameCell(name: string, role: string): string {
  return `${escHtml(name)}${role ? ` <span style="font-weight:600;color:#7a8085;font-size:12px;">· ${escHtml(role)}</span>` : ""}`;
}

function personTableHtml(g: PersonGroup): string {
  const rows = g.lines.map(({ line, bill }) => {
    const job = jobParts(line);
    const detail = detailFor(line);
    const note = billStatusNote(bill);
    return `
          <tr>
            <td style="padding:9px 12px;border-top:1px solid ${RULE};vertical-align:top;">
              <div style="font-weight:700;color:${INK};font-size:13px;">${escHtml(job.name)}</div>
              ${job.where ? `<div style="font-size:12px;color:${MUTED};">${escHtml(job.where)}</div>` : ""}
              <div style="font-size:12px;color:#334155;margin-top:3px;"><strong style="font-weight:600;">${escHtml(line.label)}</strong>${detail.length ? ` · <span style="color:${MUTED};">${detail.map(escHtml).join(" · ")}</span>` : ""}</div>
              ${note ? `<div style="font-size:11.5px;color:#b45309;margin-top:3px;">${escHtml(note)}</div>` : ""}
            </td>
            <td style="padding:9px 12px;border-top:1px solid ${RULE};vertical-align:top;text-align:right;white-space:nowrap;font-weight:700;color:${INK};font-size:13px;">${fmtUSD(line.amount)}</td>
          </tr>`;
  }).join("");
  return `
      <table style="width:100%;border-collapse:collapse;margin:0 0 14px;border:1px solid ${RULE};border-radius:8px;overflow:hidden;">
        <tr style="background:#f4f5f6;">
          <td style="padding:10px 12px;font-weight:800;color:${INK};font-size:14px;">${nameCell(g.name, g.role)}</td>
          <td style="padding:10px 12px;text-align:right;font-weight:800;color:${INK};font-size:14px;white-space:nowrap;">${fmtUSD(g.total)}</td>
        </tr>${rows}
      </table>`;
}

export function renderReportHtml(r: PayoutReport): string {
  const s = summarize(r.bills);
  const groups = groupByPerson(r.bills);
  const when = s.firstDay && s.firstDay === s.lastDay ? `${weekday(s.firstDay)}, ${fmtDay(s.firstDay)}` : daySpan(s.firstDay, s.lastDay);
  const intro = introText(r);
  const heldBox = s.held.bills
    ? `
      <p style="margin:0 0 14px;font-size:13px;color:#92400e;background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:10px 12px;">
        ${fmtUSD(s.held.total)} of this (${plural(s.held.bills, "bill")}) is <strong>held</strong>: it goes to Books when the hold
        clears. The reason is under each held line.
      </p>`
    : "";
  const summaryRows = groups.map((g) => `
          <tr>
            <td style="padding:9px 12px;border-bottom:1px solid ${RULE};font-weight:700;color:${INK};">${nameCell(g.name, g.role)}</td>
            <td style="padding:9px 12px;border-bottom:1px solid ${RULE};text-align:right;color:#475569;">${g.jobs || ""}</td>
            <td style="padding:9px 12px;border-bottom:1px solid ${RULE};text-align:right;font-weight:800;color:${INK};white-space:nowrap;">${fmtUSD(g.total)}</td>
          </tr>`).join("");

  return `
  <div style="font-family:-apple-system,'Segoe UI',Inter,Arial,sans-serif;max-width:640px;margin:0 auto;color:#2a2e30;">
    <div style="background:${ORANGE};color:#fff;padding:20px 24px;border-radius:12px 12px 0 0;">
      <div style="font-size:22px;font-weight:800;">💵 Commission Payouts Approved</div>
      <div style="font-size:13px;opacity:.92;">${escHtml(when)} · ${fmtUSD(s.total)} to ${plural(s.people, "person", "people")}</div>
    </div>
    <div style="border:1px solid #e5e7eb;border-top:0;border-radius:0 0 12px 12px;padding:18px 20px;">
      <p style="margin:0 0 14px;font-size:14px;color:#475569;">
        ${intro ? `${escHtml(intro)} ` : ""}${escHtml(PAY_NOTE)}
      </p>${heldBox}
      <p style="margin:0 0 16px;">
        <a href="${escHtml(r.boardUrl)}" style="display:inline-block;background:#2a2e30;color:#fff;text-decoration:none;font-weight:700;font-size:14px;padding:10px 16px;border-radius:8px;">Open the Commission board →</a>
      </p>
      <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid ${RULE};border-radius:8px;overflow:hidden;margin:0 0 20px;">
        <thead>
          <tr style="background:#f4f5f6;">
            <th style="${TH}text-align:left;">Person</th>
            <th style="${TH}text-align:right;">Jobs</th>
            <th style="${TH}text-align:right;">Total</th>
          </tr>
        </thead>
        <tbody>${summaryRows || `<tr><td colspan="3" style="padding:18px 12px;color:${MUTED};text-align:center;">Nothing left to pay: every approval was withdrawn.</td></tr>`}</tbody>
        <tfoot>
          <tr>
            <td style="padding:12px;font-weight:800;color:${INK};">Total approved</td>
            <td style="padding:12px;text-align:right;color:#475569;">${s.jobs || ""}</td>
            <td style="padding:12px;text-align:right;font-weight:900;color:${ORANGE};font-size:16px;white-space:nowrap;">${fmtUSD(s.total)}</td>
          </tr>
        </tfoot>
      </table>
      <div style="font-size:15px;font-weight:800;color:${INK};margin:0 0 10px;">Each person's jobs</div>${groups.map(personTableHtml).join("")}
      <p style="margin:14px 0 0;font-size:11.5px;color:#94a3b8;">${escHtml(footerText(r))}</p>
    </div>
  </div>`;
}

// ── plain text ──────────────────────────────────────────────────────────────────────────────────────────────

export function renderReportText(r: PayoutReport): string {
  const s = summarize(r.bills);
  const groups = groupByPerson(r.bills);
  const out: string[] = [];
  out.push(`Commission payouts approved — ${daySpan(s.firstDay, s.lastDay)}`);
  out.push(`${fmtUSD(s.total)} to ${plural(s.people, "person", "people")} · ${plural(s.jobs, "job")}`);
  for (const a of approvals(r.bills)) out.push(`${approvalSentence(a)}.`);
  if (r.sentBy) out.push(`Sent by ${tidyName(r.sentBy)} from the Commission board.`);
  if (s.held.bills) out.push(`Held until the hold clears: ${fmtUSD(s.held.total)} (${plural(s.held.bills, "bill")})`);
  out.push("");
  for (const g of groups) out.push(`${g.name}${g.role ? ` (${g.role})` : ""}: ${fmtUSD(g.total)}`);
  out.push(`Total approved: ${fmtUSD(s.total)}`);
  for (const g of groups) {
    out.push("", `${g.name.toUpperCase()}${g.role ? ` · ${g.role}` : ""} · ${fmtUSD(g.total)}`);
    for (const { line, bill } of g.lines) {
      const job = jobParts(line);
      out.push(`  ${job.name}${job.where ? ` — ${job.where}` : ""}`);
      out.push(`    ${line.label}: ${fmtUSD(line.amount)}`);
      const detail = detailFor(line);
      if (detail.length) out.push(`    ${detail.join(" · ")}`);
      const note = billStatusNote(bill);
      if (note) out.push(`    ${note}`);
    }
  }
  out.push("", PAY_NOTE);
  out.push(`Commission board: ${r.boardUrl}`);
  out.push(footerText(r));
  return out.join("\n");
}
